import { Link, useParams } from "react-router-dom";
import { CITIES } from "@/data/content";

export default function LocationDetail() {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);

  if (!city) {
    return (
      <main className="py-24 bg-white" data-testid="location-not-found">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="editorial-title text-3xl text-[#0a1b2a]">We don't have a page for that area yet.</h1>
          <p className="mt-4 text-slate-600">
            We may still serve your neighborhood.{" "}
            <Link to="/#contact" className="text-sky-700 font-semibold hover:underline" data-testid="location-notfound-cta">
              Request a free water analysis
            </Link>{" "}
            and we'll confirm coverage.
          </p>
          <Link to="/locations" className="inline-block mt-6 text-sm text-slate-600 hover:text-slate-900" data-testid="location-back-link">
            ← Back to all service areas
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid={`location-page-${city.slug}`}>
      <section className="py-20 bg-[#0a1b2a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/locations" className="text-xs text-sky-300 hover:text-sky-200 font-medium" data-testid="location-back-link">
            ← All Oklahoma service areas
          </Link>
          <span className="block text-xs font-bold uppercase tracking-widest text-sky-300 mt-6">{city.region}</span>
          <h1 className="editorial-title text-4xl sm:text-5xl font-normal mt-2">
            {city.name} water, explained honestly
          </h1>
          <p className="mt-4 text-slate-300 text-lg max-w-3xl">{city.headline}.</p>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h2 className="editorial-title text-2xl sm:text-3xl text-[#0a1b2a] font-normal">
              Where {city.name} water comes from — and what that means
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{city.intro}</p>

            <h3 className="text-lg font-bold text-slate-900 mt-10">What {city.name} homeowners commonly notice</h3>
            <ul className="mt-4 space-y-3">
              {city.issues.map((issue) => (
                <li key={issue} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-800 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">✓</span>
                  {issue}
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 rounded-2xl bg-[#f8fbff] border border-slate-200">
              <h3 className="text-base font-bold text-slate-900">Before you buy any system in {city.name}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{city.beforeYouBuy}</p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#0f2b44] rounded-3xl p-8 text-white sticky top-28">
              <span className="text-xs font-bold uppercase tracking-widest text-sky-300">Don't Guess. Test.</span>
              <h3 className="text-xl font-bold mt-2">What we test in {city.name} homes</h3>
              <ul className="mt-4 space-y-2.5">
                {city.testFor.map((t) => (
                  <li key={t} className="flex items-center gap-3 text-sm text-slate-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0"></span>
                    {t}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                The exact testing list depends on your water source and what you're experiencing — we test
                what's relevant, at your kitchen sink, in about 30–45 minutes.
              </p>
              <Link to="/#contact" data-testid="location-cta"
                className="mt-6 flex items-center justify-center w-full py-3.5 rounded-xl text-sm font-bold text-slate-950 bg-sky-300 hover:bg-sky-200 transition">
                GET MY FREE WATER ANALYSIS
              </Link>
              <p className="text-center text-[11px] text-slate-400 mt-3">Free. 30–45 minutes. No obligation.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-[#f4f9fd]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xs font-bold uppercase tracking-widest text-sky-800 mb-5">Also serving</h3>
          <div className="flex flex-wrap gap-3">
            {CITIES.filter((c) => c.slug !== city.slug).map((c) => (
              <Link key={c.slug} to={`/locations/${c.slug}`} data-testid={`location-related-${c.slug}`}
                className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:border-sky-300 hover:text-sky-800 transition">
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
